/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 26/04/22
 */

import { padStart } from 'lodash';

/**
 * Get number of weekdays between two dates, inclusive of both
 * @param startDate Start date
 * @param endDate End date
 */
export const weekdaysBetween = (startDate: Date, endDate: Date): number => {
    // Return 0 if End is before Start
    if (endDate < startDate) {
        return 0;
    }

    const s = new Date(startDate);
    // Include first day
    s.setDate(s.getDate() - 1);
    const e = endDate;

    const diffDays = Math.floor((e.getTime() - s.getTime()) / 86400000);
    const weeksBetween = Math.floor(diffDays / 7);
    let adjust;
    if (s.getDay() === e.getDay()) {
        adjust = 0;
    } else if (s.getDay() === 0 && e.getDay() === 6) {
        adjust = 5;
    } else if (s.getDay() === 6 && e.getDay() === 0) {
        adjust = 0;
    } else if (e.getDay() === 6 || e.getDay() === 0) {
        adjust = 5 - s.getDay();
    } else if (s.getDay() === 0 || s.getDay() === 6) {
        adjust = e.getDay();
    } else if (e.getDay() > s.getDay()) {
        adjust = e.getDay() - s.getDay();
    } else {
        adjust = 5 + e.getDay() - s.getDay();
    }
    return weeksBetween * 5 + adjust;
};

export const toISODateString = (date: Date) => {
    return date.toISOString().split('T')[0];
};

export const toTZUnawareISODateString = (date: Date) => {
    return `${date.getFullYear()}-${padStart('' + (date.getMonth() + 1), 2, '0')}-${padStart(
        '' + date.getDate(),
        2,
        '0',
    )}`;
};

export function formatDate(date: string | Date): string {
    if (typeof date === 'string') date = new Date(date);

    return date.toLocaleDateString('pt-PT');
}

export const toDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export function dayEnd(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

export function dayStart(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

export function dateAdd(
    date: Date,
    {
        years = 0,
        months = 0,
        days = 0,
        hours = 0,
        minutes = 0,
        seconds = 0,
    }: { years?: number; months?: number; days?: number; hours?: number; minutes?: number; seconds?: number },
): Date {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years, d.getMonth() + months, d.getDate() + days);
    d.setHours(d.getHours() + hours, d.getMinutes() + minutes + d.getSeconds() + seconds);
    return d;
}
